import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { http } from "../../lib/api";
import PremiumLayout from "./PremiumLayout";
import PhotoCarousel from "./PhotoCarousel";

function money(n) {
  const v = Number(n) || 0;
  if (v >= 10_000_000) return `₹${(v / 10_000_000).toFixed(2)} Cr`;
  if (v >= 100_000)    return `₹${(v / 100_000).toFixed(2)} L`;
  return `₹${v.toLocaleString("en-IN")}`;
}

const FALLBACK_PHOTOS = [
  "https://images.unsplash.com/photo-1613490493576-7fde63acd811?w=1600&auto=format&fit=crop&q=80",
];

export default function PremiumPropertyDetail() {
  const { id } = useParams();
  const [p, setP] = useState(null);

  useEffect(() => {
    http.get("/marketing/site").then(({ data }) => {
      setP((data.properties || []).find((x) => x.id === id) || null);
    }).catch(() => {});
  }, [id]);

  if (!p) {
    return (
      <PremiumLayout>
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-32 text-center gold">Loading…</div>
      </PremiumLayout>
    );
  }

  const photos = (p.photos && p.photos.length > 0) ? p.photos : FALLBACK_PHOTOS;
  const hasCoords = p.latitude != null && p.longitude != null;
  const mapSrc = hasCoords
    ? `https://www.openstreetmap.org/export/embed.html?bbox=${p.longitude - 0.007},${p.latitude - 0.005},${p.longitude + 0.007},${p.latitude + 0.005}&layer=mapnik&marker=${p.latitude},${p.longitude}`
    : `https://www.openstreetmap.org/export/embed.html?bbox=77.585,12.965,77.605,12.978&layer=mapnik&marker=12.9716,77.5946`;
  const mapOpen = hasCoords
    ? `https://www.openstreetmap.org/?mlat=${p.latitude}&mlon=${p.longitude}#map=16/${p.latitude}/${p.longitude}`
    : `https://www.openstreetmap.org/search?query=${encodeURIComponent(p.location || "")}`;

  return (
    <PremiumLayout>
      {/* Photo carousel hero */}
      <section className="w-full" data-testid="property-hero">
        <PhotoCarousel photos={photos} interval={5000} alt={p.title}
                       className="w-full aspect-[16/8] md:aspect-[21/9]" />
      </section>

      {/* Header */}
      <section className="max-w-[1400px] mx-auto px-6 md:px-10 pt-12 pb-8">
        <div className="gold text-[11px] tracking-[.16em] uppercase">{p.location || "—"}</div>
        <div className="mt-4 flex items-end justify-between gap-6 flex-wrap">
          <h1 className="text-5xl md:text-6xl leading-[1.02]">{p.title}</h1>
          <div className="gold border border-[#C9A96E] px-4 py-1.5 text-[11px] tracking-[.16em] uppercase">
            {p.status || "Available"}
          </div>
        </div>

        {/* Price + Area — same color, font, height (per spec) */}
        <div className="mt-8 flex flex-wrap gap-x-14 gap-y-4 items-baseline"
             data-testid="property-price-area">
          <div>
            <div className="text-[11px] tracking-[.16em] uppercase text-[#9A9284]">Price</div>
            <div className="text-4xl mt-1 gold" style={{ lineHeight: 1 }}>{money(p.price)}</div>
          </div>
          <div>
            <div className="text-[11px] tracking-[.16em] uppercase text-[#9A9284]">Area</div>
            <div className="text-4xl mt-1 gold" style={{ lineHeight: 1 }}>
              {p.area_sqft ? `${p.area_sqft.toLocaleString("en-IN")} sqft` : "—"}
            </div>
          </div>
        </div>
      </section>

      {/* About */}
      <section className="max-w-[1400px] mx-auto px-6 md:px-10 py-12 grid md:grid-cols-3 gap-10 border-t border-[#1F1F1F]">
        <div className="md:col-span-2">
          <div className="gold text-[11px] tracking-[.16em] uppercase">About this property</div>
          <p className="text-xl md:text-2xl mt-4 leading-[1.5] cream">
            {p.description || "A carefully finished home built by our studio — ready for immediate occupation."}
          </p>
          {(p.amenities || []).length > 0 && (
            <div className="mt-10">
              <div className="gold text-[11px] tracking-[.16em] uppercase">Amenities</div>
              <div className="mt-4 grid sm:grid-cols-2 gap-2">
                {p.amenities.map((a) => (
                  <div key={a} className="border border-[#2A2A2A] px-4 py-3 text-sm text-[#DDD5C4]">
                    <span className="gold mr-2">•</span>{a}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
        <aside className="md:col-span-1">
          <div className="border border-[#1F1F1F] p-6 sticky top-24">
            <div className="gold text-[11px] tracking-[.16em] uppercase">Enquire</div>
            <div className="text-2xl mt-3">Book a viewing</div>
            <div className="mt-4 space-y-1.5 text-[#DDD5C4]">
              <div>{p.contact || "+91 90000 00000"}</div>
              <div><a href="mailto:hello@dreamhomeplanners.com" className="gold-link">hello@dreamhomeplanners.com</a></div>
            </div>
            <Link to="/contact" data-testid="property-book-viewing"
                  className="mt-6 block text-center bg-[#C9A96E] text-[#0A0A0A] px-5 py-3 font-semibold hover:bg-[#DFBB7C]">
              Contact the studio →
            </Link>
            <Link to="/properties" className="mt-3 block text-center border border-[#2A2A2A] text-[#DDD5C4] px-5 py-3 hover:border-[#C9A96E] hover:text-[#C9A96E]">
              ← Back to Properties
            </Link>
          </div>
        </aside>
      </section>

      {/* Location map */}
      <section className="border-t border-[#1F1F1F]" data-testid="property-map-section">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-12">
          <div className="gold text-[11px] tracking-[.16em] uppercase">Location</div>
          <h2 className="text-4xl md:text-5xl mt-3">{p.location || "On the map"}</h2>
        </div>
        <div className="relative w-full h-[440px] overflow-hidden border-y border-[#1F1F1F]">
          <iframe
            title={`${p.title} — location`}
            src={mapSrc}
            className="absolute inset-0 w-full h-full"
            style={{ filter: "invert(0.92) hue-rotate(180deg) grayscale(0.4) brightness(0.85) contrast(1.05)" }}
            loading="lazy"
          />
          <a href={mapOpen} target="_blank" rel="noreferrer"
             data-testid="property-map-open"
             className="absolute bottom-6 left-6 gold border border-[#C9A96E] bg-[#0A0A0A]/85 px-4 py-2 hover:bg-[#C9A96E] hover:text-[#0A0A0A]">
            Open in Maps ↗
          </a>
        </div>
      </section>

      {/* Photo gallery */}
      {photos.length > 1 && (
        <section className="max-w-[1600px] mx-auto px-3 md:px-4 py-16">
          <div className="max-w-[1400px] mx-auto px-3 md:px-6 pb-6">
            <div className="gold text-[11px] tracking-[.16em] uppercase">Gallery</div>
            <h2 className="text-4xl md:text-5xl mt-3">More views</h2>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-1.5 md:gap-2">
            {photos.map((src, idx) => (
              <div key={`${src}-${idx}`} className="aspect-[4/3] overflow-hidden">
                <img src={src} alt="" className="w-full h-full object-cover hover:scale-105 transition-transform duration-[1.2s]" />
              </div>
            ))}
          </div>
        </section>
      )}
    </PremiumLayout>
  );
}
